<template>
  <div class="full-width">
    <h4
      v-if="(title || $te(`document.${purpose}`)) && !props.noTitle"
      :class="props.subtitle ? 'row items-center' : 're-mb row items-center'"
    >
      {{ title || $t(`document.${purpose}`, 1) }}
      <re-tooltip
        v-if="$te(`document.${purpose}_tooltip`) && !isEmpty($t(`document.${purpose}_tooltip`))"
        :label="$t(`document.${purpose}_tooltip`)"
      />
    </h4>

    <p
      v-if="props.subtitle"
      class="re-mb text-secondary"
    >
      {{ props.subtitle }}
    </p>

    <q-file
      :ref="`file-${purpose}`"
      borderless
      :accept="props.accept.join(',')"
      :readonly="readOnlyComputed"
      :max-file-size="props.maxFileSize"
      class="full-width"
      hide-bottom-space
      :multiple="props.multiple"
      :disable="props.disabled"
      @update:model-value="(files) => emit('upload', normalizeFiles(files))"
      @rejected="
        (rejectedEntries) =>
          $q.notify({
            type: enums.feedbackType.NEGATIVE,
            // TODO: use custom error messages:
            message: `${rejectedEntries.length} file(s) did not pass validation constraints`,
          })
      "
    >
      <div class="full-width">
        <div class="drop-area text-center">
          <template v-if="props.disabled && props.disabledLabel">
            <p class="text-body1 text-secondary q-ma-none">
              {{ props.disabledLabel }}
            </p>
          </template>

          <template v-else>
            <p class="text-body1 text-primary q-ma-none">
              {{ $t(`valuer_tool.commons.upload.button_title`) }}
            </p>

            <p class="q-ma-none text-black">
              {{ $t(`valuer_tool.commons.upload.button_subtitle`) }}
            </p>

            <p class="text-secondary q-pt-sm q-ma-none">
              {{
                $t('valuer_tool.commons.upload.max', {
                  size: $format.integer(props.maxFileSize / (1024 * 1024)),
                })
              }}
            </p>
          </template>
        </div>
      </div>
    </q-file>
  </div>
</template>

<script setup>
import { isEmpty } from 'lodash-es'
import { computed } from 'vue'

import { useReadOnly } from '@/composables/useReadOnly'
import enums from '@/plugins/enums'

const props = defineProps({
  purpose: {
    type: String,
    default: undefined,
  },
  noTitle: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: null,
  },
  subtitle: {
    type: String,
    default: null,
  },
  maxFileSize: {
    type: Number,
    default: 50 * 1024 * 1024,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: null,
  },
  multiple: {
    type: Boolean,
    default: true,
  },
  disabledLabel: {
    type: String,
    default: '',
  },
  accept: {
    type: Array,
    default: () => [...enums.fileAccept.DEFAULT],
  },
})

const emit = defineEmits(['upload'])

// QFile emits a bare File (not an array) when `multiple` is false; always hand consumers an array.
function normalizeFiles(files) {
  if (!files)
    return []
  return Array.isArray(files) ? [...files] : [files]
}

const { isReadOnly } = useReadOnly()
const readOnlyComputed = computed(() => props.readonly ?? isReadOnly.value)
</script>

<style lang="scss" scoped>
.drop-area {
  border: 2px dashed $border;
  border-radius: $generic-border-radius;
  // margin-top: 24px;
  padding: 16px;
}

.q-field--error .container {
  border: 2px solid var(--q-negative);
}

// FIXME: this hack removed the file list
// Normally should be doable through the #selected template (doesn't work)
:deep(.q-field__native) {
  width: 0px !important;
  height: 0px !important;
}
</style>
